<template>
  <div class="w-full relative">
    <!-- Customers see this on the share-link page too, so it never says "canvas".
         Height comes from the consumer: a fixed block in the form, the whole screen elsewhere. -->
    <Loading v-if="!isReady" class="absolute inset-0 p-16">
      <p class="text-base text-gray-800">Loading preview...</p>
    </Loading>
    <iframe
      ref="iframeRef"
      :src="url"
      title="Preview"
      class="w-full h-full border-none transition-opacity duration-300"
      :class="{ 'opacity-0 pointer-events-none': !isReady }"
      frameborder="0"
      allowfullscreen
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import Loading from "@/components/UI/Loading/Loading.vue";
import { CANVAS_EVENTS, SCREENSHOT_TIMEOUT } from "@/consts/previewCanvas";
import { sceneChanges } from "@/utils/sceneChanges";

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  token: {
    type: String,
    default: "",
  },
  // A previously saved scene to restore; the iframe wants it as an object.
  scene: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: "design",
    validator: (value) => ["design", "preview"].includes(value),
  },
});

// scene-changes: what the user has changed about the design so far, e.g.
// [{ subject: "photo", change: "moved" }]; an empty list once it is back as it was.
const emit = defineEmits(["scene-changes"]);

const iframeRef = ref(null);
const isReady = ref(false);
// The iframe reports every change itself, so the latest one is what gets saved.
let modifiedScene = null;
// Only one screenshot can be in flight — the save button asks for it.
let pendingScreenshot = null;
// The designs that count as "unchanged": the saved one handed in, and the one the canvas
// reports once it has loaded (its own serialisation of the same design).
let unchangedScenes = [];
let reportedChanges = "[]";

const canvasOrigin = computed(() => new URL(props.url).origin);

// postMessage structured-clones what it sends and throws DataCloneError on a Vue proxy —
// which is what any object prop, like the scene, arrives as. The scene is plain JSON data,
// so a JSON round trip hands over an ordinary copy without losing anything.
const toPlain = (value) => JSON.parse(JSON.stringify(value));

const postToCanvas = (event) => {
  iframeRef.value?.contentWindow?.postMessage({ event: toPlain(event) }, canvasOrigin.value);
};

const setUpCanvas = () => {
  if (props.scene) {
    postToCanvas({ type: CANVAS_EVENTS.setScene, scene: props.scene });
  }
  if (props.token) {
    postToCanvas({ type: CANVAS_EVENTS.setToken, token: props.token });
  }
  postToCanvas({ type: CANVAS_EVENTS.setViewportMode, mode: props.mode });
  // The canvas's own report first: it is what the user saw when the canvas opened.
  unchangedScenes = [modifiedScene, props.scene].filter(Boolean);
  isReady.value = true;
};

// scene_modified arrives for far more than user edits — while loading, when a scene is
// re-applied, when it is re-serialised — so the design itself is compared. It is
// unchanged if it matches any unchanged design; what changed is told against the first,
// the design the user saw when the canvas opened.
const trackChanges = (scene) => {
  if (!unchangedScenes.length) {
    unchangedScenes = [scene];
    return;
  }
  const unchanged = unchangedScenes.some((known) => !sceneChanges(known, scene).length);
  const changes = unchanged ? [] : sceneChanges(unchangedScenes[0], scene);
  const serialised = JSON.stringify(changes);
  if (serialised === reportedChanges) return;
  reportedChanges = serialised;
  emit("scene-changes", changes);
};

// Until the user changes anything, the scene is the one the canvas was opened with.
const getScene = () => modifiedScene || props.scene;

// Once a design is saved, it is the one changes are told against from then on. Pass
// the scene that was actually sent: edits made while the save was on its way are
// then still reported as changes rather than taken for saved.
const markSaved = (savedScene = getScene()) => {
  unchangedScenes = [savedScene].filter(Boolean);
  reportedChanges = null;
  const current = getScene();
  if (current && unchangedScenes.length) {
    trackChanges(current);
  } else {
    reportedChanges = "[]";
    emit("scene-changes", []);
  }
};

const capture = () =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      pendingScreenshot = null;
      reject(new Error("The canvas did not send a screenshot in time"));
    }, SCREENSHOT_TIMEOUT);

    pendingScreenshot = { resolve, timeout };
    postToCanvas({ type: CANVAS_EVENTS.getScreenshot });
  });

const handleMessage = (event) => {
  if (event.origin !== canvasOrigin.value) return;
  if (!iframeRef.value || event.source !== iframeRef.value.contentWindow) return;

  const message = event.data?.event;
  if (!message?.type) return;

  switch (message.type) {
    case CANVAS_EVENTS.ready:
      setUpCanvas();
      break;
    case CANVAS_EVENTS.sceneModified:
      modifiedScene = message.scene;
      // While loading, the canvas reports its template and then the loaded project as
      // scene_modified; only what comes after all_actions_complete can be the user's doing.
      if (isReady.value) trackChanges(message.scene);
      break;
    case CANVAS_EVENTS.screenshot:
      if (!pendingScreenshot) return;
      clearTimeout(pendingScreenshot.timeout);
      pendingScreenshot.resolve({ scene: getScene(), screenshot: message.dataUrl });
      pendingScreenshot = null;
      break;
    default:
  }
};

onMounted(() => {
  window.addEventListener("message", handleMessage);
});

onUnmounted(() => {
  window.removeEventListener("message", handleMessage);
});

defineExpose({ capture, getScene, markSaved });
</script>
